import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Subscription } from '../../types';

interface Props {
  subscriptions: Subscription[];
}

function getDismissKey(ids: string[]): string {
  return `dismissed_suspended_${ids.sort().join(',')}`;
}

export default function SuspendedBanner({ subscriptions }: Props) {
  const suspended = subscriptions.filter(
    (s) => s.status === 'suspended' || s.status === 'archived'
  );

  const dismissKey = getDismissKey(suspended.map((s) => s.id));
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey) === 'true';
    } catch {
      return false;
    }
  });

  if (suspended.length === 0 || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(dismissKey, 'true');
    } catch {
      // localStorage unavailable
    }
  };

  const count = suspended.length;
  const label = count === 1 ? 'suspended service' : 'suspended services';

  return (
    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-amber-800">
        <svg
          className="w-5 h-5 text-amber-500 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <span>
          You have {count} {label}.{' '}
          <Link
            to="/billing"
            className="font-medium text-amber-900 underline hover:text-amber-700"
          >
            Go to Billing
          </Link>{' '}
          to reactivate.
        </span>
      </div>
      <button
        onClick={handleDismiss}
        className="text-amber-400 hover:text-amber-600 flex-shrink-0 ml-4"
        aria-label="Dismiss"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
